import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { EMPTY, of } from 'rxjs';
import {
  catchError,
  map,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { Router } from '@angular/router';
import { ErrorService, LoaderService } from '@common/util-foundation';
import { BundleApplianceDetails, ItemType, SkyBasket } from '@sky/util-models';
import { BasketApiService } from '../../services/basket-api.service';
import { BasketPartialState } from '../basket.reducer';
import * as DevicesActions from './../devices/devices.actions';
import * as QuotesSelectors from './../quotes/quotes.selectors';

@Injectable()
export class DevicesEffects {
  addDevicesToBasket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DevicesActions.addDevicesToBasket),
      withLatestFrom(
        this.store$.pipe(select(QuotesSelectors.getBasketItemQuoteApiResult))
      ),
      switchMap(([{ devices }, basketItemQuoteApiResult]) => {
        if (!basketItemQuoteApiResult) return EMPTY;

        return this.basketApiService
          .addAppliances({
            basketId: basketItemQuoteApiResult.basketId,
            itemId: basketItemQuoteApiResult.itemId,
            itemType: ItemType.QuoteSkyMobile,
            appliances: devices,
          })
          .pipe(
            map((addAppliancesResponse) =>
              DevicesActions.addDevicesToBasketSuccess({
                devices: this.getDevices(addAppliancesResponse, devices),
              })
            ),
            catchError((error: HttpErrorResponse) => {
              this.handleError();
              return of(DevicesActions.addDevicesToBasketFailure({ error }));
            })
          );
      })
    )
  );

  addDevicesToBasketRedirect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DevicesActions.addDeviceToBasketRedirect),
      withLatestFrom(
        this.store$.pipe(select(QuotesSelectors.getBasketItemQuoteApiResult))
      ),
      switchMap(([{ devices }, basketItemQuoteApiResult]) => {
        if (!basketItemQuoteApiResult) return EMPTY;

        return this.basketApiService
          .addAppliances({
            basketId: basketItemQuoteApiResult.basketId,
            itemId: basketItemQuoteApiResult.itemId,
            itemType: ItemType.QuoteSkyMobile,
            appliances: devices,
          })
          .pipe(
            map((addAppliancesResponse) =>
              DevicesActions.addDevicesToBasketSuccess({
                devices: this.getDevices(addAppliancesResponse, devices),
              })
            ),
            tap(() => {
              this.router.navigateByUrl('/checkout/personal-details');
            }),
            catchError((error: HttpErrorResponse) => {
              this.handleError();
              return of(DevicesActions.addDevicesToBasketFailure({ error }));
            })
          );
      })
    )
  );

  removeDevicesFromBasket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DevicesActions.deleteDevicesFromBasket),
      withLatestFrom(
        this.store$.pipe(select(QuotesSelectors.getBasketItemQuoteApiResult))
      ),
      switchMap(([{ deviceIds, redirectBack }, basketItemQuoteApiResult]) => {
        if (!basketItemQuoteApiResult) return EMPTY;

        return this.basketApiService
          .removeAppliances({
            basketId: basketItemQuoteApiResult.basketId,
            itemId: basketItemQuoteApiResult.itemId,
            itemType: ItemType.QuoteSky,
            appliances: deviceIds,
          })
          .pipe(
            map(() =>
              DevicesActions.deleteDevicesFromBasketSuccess({
                deviceIds: deviceIds.map((item) => item.applianceId),
                redirectBack,
              })
            ),
            catchError((error: HttpErrorResponse) => {
              this.handleError();
              return of(
                DevicesActions.deleteDevicesFromBasketFailure({ error })
              );
            })
          );
      })
    )
  );

  redirectAfterSuccessDelete$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DevicesActions.deleteDevicesFromBasketSuccess),
        tap(({ redirectBack }) => {
          if (redirectBack) {
            this.router.navigate(['/add-device'], {
              queryParamsHandling: 'preserve',
            });
          }
        })
      ),
    { dispatch: false }
  );

  showLoader$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          DevicesActions.addDevicesToBasket,
          DevicesActions.addDeviceToBasketRedirect,
          DevicesActions.deleteDevicesFromBasket
        ),
        tap(() => this.loaderService.showLoader())
      ),
    { dispatch: false }
  );

  hideLoader$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          DevicesActions.addDevicesToBasketSuccess,
          DevicesActions.addDevicesToBasketFailure,
          DevicesActions.deleteDevicesFromBasketSuccess,
          DevicesActions.deleteDevicesFromBasketFailure
        ),
        tap(() => this.loaderService.hideLoader())
      ),
    { dispatch: false }
  );

  private getDevices(skyBasket: SkyBasket, devices: BundleApplianceDetails[]) {
    return skyBasket.items[0].data.applianceDetails.bundleAppliances.map(
      (bundleAppliances) => {
        return {
          ...devices.find(
            (device) => device.applianceCode === bundleAppliances.applianceCode
          ),
          ...bundleAppliances,
        };
      }
    );
  }

  handleError() {
    this.errorService.handleError();
  }

  constructor(
    private actions$: Actions,
    private store$: Store<BasketPartialState>,
    private readonly errorService: ErrorService,
    private basketApiService: BasketApiService,
    private loaderService: LoaderService,
    private router: Router
  ) {}
}
